function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

// Desktop mega menu hover delay (prevents accidental menu switching when moving diagonally)
(function initMegaMenuHoverDelay() {
  const DESKTOP_MIN_WIDTH = 990;
  const OPEN_DELAY = 110;  // 150–300
  const CLOSE_DELAY = 200; // 120–220

  const isDesktop = () => window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches;

  const getMegaDetails = () =>
    Array.from(document.querySelectorAll('.header__inline-menu header-menu details.mega-menu'));

  const setAriaExpanded = (detailsEl, expanded) => {
    const summary = detailsEl.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const closeAllExcept = (keep) => {
    getMegaDetails().forEach((d) => {
      if (d !== keep) {
        d.removeAttribute('open');
        setAriaExpanded(d, false);
      }
    });
  };

  const wireOne = (detailsEl) => {
    if (detailsEl.dataset.hoverDelayBound === 'true') return;
    detailsEl.dataset.hoverDelayBound = 'true';

    const headerMenu = detailsEl.closest('header-menu');
    const summary = detailsEl.querySelector('summary');
    const content = detailsEl.querySelector('.mega-menu__content');

    if (!headerMenu || !summary || !content) return;

    // Disable Debutify hover logic if it relies on data-hover
    // We will manage hover ourselves with delay
    headerMenu.setAttribute('data-hover', 'false');

    let openTimer = null;
    let closeTimer = null;

    const clearOpen = () => {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
    };

    const clearClose = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    };

    const openWithDelay = () => {
      if (!isDesktop()) return;
      if (detailsEl.hasAttribute('open')) return;

      clearClose();
      clearOpen();

      openTimer = setTimeout(() => {
        closeAllExcept(detailsEl);
        detailsEl.setAttribute('open', '');
        setAriaExpanded(detailsEl, true);
      }, OPEN_DELAY);
    };

    const closeWithDelay = () => {
      if (!isDesktop()) return;

      clearOpen();
      clearClose();

      closeTimer = setTimeout(() => {
        detailsEl.removeAttribute('open');
        setAriaExpanded(detailsEl, false);
      }, CLOSE_DELAY);
    };

    // Use details as the hover zone so diagonal movement into dropdown does not trigger close
    const onEnter = (e) => {
      if (!isDesktop()) return;
      clearClose();
      openWithDelay();
    };

    const onLeave = (e) => {
      if (!isDesktop()) return;
      closeWithDelay();
    };

    detailsEl.addEventListener('pointerenter', onEnter);
    detailsEl.addEventListener('pointerleave', onLeave);

    // Click should open immediately and close others
    summary.addEventListener('click', () => {
      if (!isDesktop()) return;
      setTimeout(() => {
        if (detailsEl.hasAttribute('open')) {
          closeAllExcept(detailsEl);
          setAriaExpanded(detailsEl, true);
        }
      }, 0);
    });
  };

  const init = () => {
    if (!isDesktop()) return;
    getMegaDetails().forEach(wireOne);
  };

  init();
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('resize', init);
})();

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

    headerTransparent(OpenClose) {
    const stickyHeader = document.querySelector('sticky-header');
    const dbtfyHeader = document.querySelector('.dbtfy-header');

    if (!stickyHeader || !dbtfyHeader) return;

    if (dbtfyHeader.classList.contains('scrolled-past-header')) return;

    const headerColor = stickyHeader;
    const classList = headerColor.classList;

    const removeColorClasses = () => {
      Array.from(classList).forEach((className) => {
        if (className.startsWith('dbtfy-color-') || className.startsWith('color-')) {
          classList.remove(className);
        }
      });
    };

    if (OpenClose === 'open') {
      // Menu opens, force solid header scheme
      stickyHeader.classList.remove('dbtfy-header--transparent');
      dbtfyHeader.classList.remove('header--transparent');

      removeColorClasses();

      classList.add(
        `dbtfy-color-${headerColor.dataset.colorScheme}`,
        `color-${headerColor.dataset.customColorScheme}`
      );
    } else {
      // Menu closes, decide whether to be transparent or solid
      removeColorClasses();

      const hasDebutifyTransparent =
        window.Debutify &&
        Debutify.widgets &&
        Debutify.widgets['header'] &&
        Debutify.widgets['header'].header_transparent === true;

      if (hasDebutifyTransparent) {
        // Use transparent header scheme if the widget says so
        stickyHeader.classList.add('dbtfy-header--transparent');
        dbtfyHeader.classList.add('header--transparent');

        classList.add(
          `dbtfy-color-${headerColor.dataset.transparentHeaderColorScheme}`,
          `color-${headerColor.dataset.customTransparentHeaderColorScheme}`
        );
      } else {
        // Otherwise stay solid after closing
        stickyHeader.classList.remove('dbtfy-header--transparent');
        dbtfyHeader.classList.remove('header--transparent');

        classList.add(
          `dbtfy-color-${headerColor.dataset.colorScheme}`,
          `color-${headerColor.dataset.customColorScheme}`
        );
      }
    }
  }


  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    if (document.querySelector('body.template-index')) {
      this.headerTransparent('open');
    }
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);

    if (document.querySelector('body.template-index')) {
      this.headerTransparent('close');
    }
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model') &&
          !this.classList.contains('product-media-modal')
        )
          this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', (e) => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      const mediaId = e.target.dataset.mediaId;
      if (modal) {
        const slider = modal.querySelector('dbtfy-slider-component');
        if (slider) {
          modal.style.visibility = 'hidden';
        }
        modal.show(button);
        if (slider) {
          const mediaPosition = this.getMediaPositionById(mediaId, modal);
          slider.goToSlide(mediaPosition);
          modal.style.visibility = 'visible';
        }
      }
    });
  }

  getMediaPositionById(mediaId, modal) {
    if (!modal.querySelector('#Slide-' + mediaId)) {
      return;
    }

    const allSlides = modal.querySelectorAll('[id^="Slide-"]');
    for (let i = 0; i < allSlides.length; i++) {
      if (allSlides[i].id === 'Slide-' + mediaId) {
        return i + 1;
      }
    }

    return -1;
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
      }
      if (this.dataset.controls == 'false' && this.dataset.autoplay == 'false') {
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    if (this.slidesPerPage < 1) this.slidesPerPage = 1;
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) {
      return;
    }

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const linked = document.body.classList.contains('dbtfy_linked_options');
    if (linked) {
      this.backInStock = document.querySelector('.dbtfy-back-in-stock:not(.debutify-handler)');
      // optionList
      if (this.backInStock) {
        this.optionList = this.querySelectorAll('option').length;
        this.disabledOptionList = this.querySelectorAll('option.dropdown_disabled').length;

        if (this.optionList == this.disabledOptionList) {
          this.backInStock.style.display = 'block';
        } else {
          this.backInStock.style.display = 'none';
        }

        // fieldset
        this.fieldset = this.querySelectorAll('fieldset input.disabled').length;
        this.disabled = this.querySelectorAll('fieldset input').length;
        if (this.fieldset == this.disabled) {
          this.backInStock.style.display = 'block';
        } else {
          this.backInStock.style.display = 'none';
        }
      }

      this.querySelectorAll('select').forEach((select) => {
        this.optionList = select.querySelectorAll('option');
        this.disabledOptionList = select.querySelectorAll('option.dropdown_disabled');

        if (this.optionList.length == this.disabledOptionList.length) {
          select.closest('.select').remove();
        } else {
          select.querySelectorAll('option.dropdown_disabled').forEach((option) => option.remove());
        }
      });

      this.querySelectorAll('fieldset').forEach((fieldset) => {
        this.fieldsetList = fieldset.querySelectorAll('input');
        this.disabledFieldsetList = fieldset.querySelectorAll('input.disabled');

        const is_disabled_checked = fieldset.querySelector('input.disabled:checked');

        fieldset.querySelectorAll('input.disabled').forEach((input) => {
          const label = fieldset.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.remove();
          }
          input.remove();
        });

        if (is_disabled_checked) {
          const target = fieldset.querySelector('input');

          if (target) {
            target.checked = true;
            target.dispatchEvent(new Event('change'));

            const event = 'custom';

            publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            });
          }
        }
      });
    }
    this.dispatchEvent(new CustomEvent('variant-selects:loaded', { bubbles: true }));

    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });

if (
  this.selectedOptionValues.length !==
  this.querySelectorAll('select option[selected], fieldset input:checked').length
) {
  this.dispatchEvent(new Event('change', { bubbles: true }));
}

    this.checkSwatch();
    setTimeout(() => {
      this.checkThumbnailCarousel();
    }, 500);
  }

  checkThumbnailCarousel() {
    const slider = this.closest('.product--thumbnail_slider');
    if (!slider) return;

    const thumbnails = slider.querySelectorAll(
      '#thumbnail-carousel.hide_variants_thumbnail .thumbnail-list_item--carousel'
    );
    const arrows = slider.querySelectorAll('#thumbnail-carousel.hide_variants_thumbnail .splide__arrows');

    if (thumbnails.length > 4) {
      arrows.forEach((el) => el.classList.remove('hidden'));
    } else {
      arrows.forEach((el) => {
        if (el) {
          el.classList.add('hidden');
        }
      });
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const value = target.value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/--+/g, '-');
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
        selectedDropdownSwatchValue.closest('[data-selected-value]').classList.remove('empty-swatch');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
        selectedDropdownSwatchValue.closest('[data-selected-value]').classList.add('empty-swatch');
      }

      selectedDropdownSwatchValue.dataset.selectedValue = value;

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
  const selectedOptions = [];

  // for each <select>, use the actually selected option, or fall back to the first option
  this.querySelectorAll('select').forEach((select) => {
    const option = select.selectedOptions[0] || select.options[0];
    if (option && option.dataset.optionValueId) {
      selectedOptions.push(option.dataset.optionValueId);
    }
  });

  // include checked radios
  this.querySelectorAll('fieldset input:checked').forEach((input) => {
    if (input && input.dataset.optionValueId) {
      selectedOptions.push(input.dataset.optionValueId);
    }
  });

  return selectedOptions;
}


  checkSwatch() {
    const swatchArray = Array.from(document.querySelectorAll('[data-color-swatch-json]')).flatMap((swatchJson) => {
      const swatches = JSON.parse(swatchJson.innerHTML);
      return swatches[0]?.toLowerCase();
    });

    this.querySelectorAll('[data-swatch-count="0"]').forEach((fieldset) => {
      fieldset.querySelectorAll('.swatch-input__input').forEach((input) => {
        const swatch = input.nextElementSibling.querySelector('.swatch--unavailable');
        if (!swatch) return;
        let inputValue = input.value.trim();
        if (Debutify.settings.ignore_swatch_case_varinat_picker) {
          inputValue = inputValue.toLowerCase();
        }
        if (!swatchArray.includes(inputValue)) {
          input.classList.add('empty-swatch');
        }
      });
    });
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('dbtfy-slider-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

function checkoutButtonHandler() {
  const checkoutButtons = document.querySelectorAll('.cart__checkout-button');

  if (checkoutButtons.length > 0) {
    const allConditionsTrue = Object.values(Debutify.checkout).every((value) => value === true);

    checkoutButtons.forEach((checkoutButton) => {
      checkoutButton.disabled = !allConditionsTrue;
    });
  }
}

// Mobile slider on off

if (!customElements.get('mobile-slider')) {
  customElements.define(
    'mobile-slider',
    class extends HTMLElement {
      constructor() {
        super();
        this.mobileCol = this.dataset.mobileCol;
        this.mobileSlider = this.dataset.mobileSlider;
        this.selectorSlider = this.querySelector(this.dataset.selectorMain);
        this.colLength = this.querySelectorAll(this.dataset.selectorLength).length;
        this.sliderButtons = this.querySelector(this.dataset.selectorArrow);

        this.MobileSwiperToggle = this.querySelector(this.dataset.selectorMobileSwiperToggle);

        if (this.colLength && this.mobileSlider && this.mobileCol) {
          this.init();
        } else {
          this.sliderButtons?.remove();
        }
      }
      init() {
        if (this.MobileSwiperToggle) {
          this.MobileSwiperToggle.style.display = 'none';
        }

        if (window.innerWidth < 989) {
          if (this.colLength > this.mobileCol && this.mobileSlider === 'true') {
            this.selectorSlider.classList.add('slider', 'slider--tablet', 'grid--peek');
            if (this.MobileSwiperToggle) {
              this.MobileSwiperToggle.style.display = 'none';
            }
          } else {
            if (this.sliderButtons) {
              this.sliderButtons.remove();
              if (this.MobileSwiperToggle) {
                this.MobileSwiperToggle.style.display = 'block';
              }
            }
          }
        }
      }
    }
  );
}

if (!customElements.get('collpasible-content')) {
  customElements.define(
    'collpasible-content',
    class extends HTMLElement {
      constructor() {
        super();
        this.firstOpen = this.dataset.firstOpen;

        this.init();
      }
      init() {
        if (this.firstOpen == 'true') {
          // add attr open .accordion details
          this.querySelector('.accordion details').setAttribute('open', '');
        }
      }
    }
  );
}

if (!customElements.get('product-card-slider')) {
  customElements.define(
    'product-card-slider',
    class extends HTMLElement {
      constructor() {
        super();
        this.selector = this.dataset.selector;
        //this.showSecondaryImage = String(this.dataset.showSecondaryImage && window.screen.width > 768);
        this.showSecondaryImage = String(this.dataset.showSecondaryImage === 'true' && window.screen.width > 768);

        this.currentImage = 0;
        this.size = +this.dataset.size;

        this.prevButton = this.querySelector(`button[name="previous-${this.selector}"]`);
        this.nextButton = this.querySelector(`button[name="next-${this.selector}"]`);
        this.images = this.querySelectorAll(`[data-product-id="${this.selector}"]`);

        if (this.prevButton) {
          this.prevButton.disabled = true;
        }

        if (this.size > 1 && this.nextButton && this.prevButton) {
          this.prevButton.classList.remove('hidden');
          this.nextButton.classList.remove('hidden');
        }

        if (this.showSecondaryImage === 'true' && this.size > 1) {
          this.images[1]?.classList.remove('hidden');
          this.querySelector('.media--transparent')?.classList.add('media--hover-effect');
        } else {
          this.images[1]?.classList.add('hidden');
          this.querySelector('.media--transparent')?.classList.remove('media--hover-effect');
        }

        // onresize
        window.addEventListener('resize', () => {
          //this.showSecondaryImage = String(this.dataset.showSecondaryImage && window.screen.width > 768);
          this.showSecondaryImage = String(this.dataset.showSecondaryImage === 'true' && window.screen.width > 768);
          if (this.showSecondaryImage === 'true' && this.size > 1) {
            this.images[1]?.classList.remove('hidden');
            this.querySelector('.media--transparent')?.classList.add('media--hover-effect');
          } else {
            this.images[1]?.classList.add('hidden');
            this.querySelector('.media--transparent')?.classList.remove('media--hover-effect');
          }
        });
      }

      revealImageWithoutHidden(index) {
        if (!this.images[index]) return;
        this.images[index].classList.remove('hidden');
        this.images[index].tagName === 'VIDEO' && this.images[index].play();
      }

      revealImage(index) {
        this.images.forEach((image, i) => {
          if (this.showSecondaryImage === 'true' && i == 0) {
            // no need to do anything here
          } else if (i === index) {
            image.classList.remove('hidden');
            image.tagName === 'VIDEO' && image.play();
          } else {
            image.classList.add('hidden');
          }

          if (index === 0 && this.showSecondaryImage === 'true') {
            this.images[1]?.classList.remove('hidden');
            this.images[1].tagName === 'VIDEO' && this.images[1].play();
          }
        });
      }

      connectedCallback() {
        this.revealImageWithoutHidden(this.currentImage);

        if (this.showSecondaryImage === 'true' && this.images.length > 1) {
          this.images[0].tagName === 'VIDEO' && this.images[0].pause();
          this.revealImageWithoutHidden(this.currentImage + 1);
        }

        this.prevButton?.addEventListener('click', () => {
          this.currentImage -= 1;
          this.revealImage(this.currentImage);

          if (this.currentImage === 0) {
            this.prevButton.disabled = true;
          } else {
            this.nextButton.disabled = false;
          }
        });

        this.nextButton?.addEventListener('click', () => {
          this.currentImage += 1;
          this.revealImage(this.currentImage);

          if (this.currentImage === this.images.length - 1) {
            this.nextButton.disabled = true;
          } else {
            this.prevButton.disabled = false;
          }
        });
      }
    }
  );
}

function getPreviousElementSiblings(element, selectorID) {
  let previousElement = element?.previousElementSibling;

  if (!previousElement) return null;

  while (previousElement) {
    if (previousElement.id && previousElement.id.startsWith(selectorID)) {
      break;
    }
    previousElement = previousElement.previousElementSibling;
  }

  if (!previousElement) return null;
  return previousElement;
}

let maxHeaderHeight = 0;
function adjustHeaderBasedOnAnnaHeadergroup() {
  if (Debutify.settings.dbtfy_sticky_header == false) return;
  let annaBar = document.querySelector('.dbtfy-announcement-bar');
  if (!annaBar?.offsetHeight) {
    annaBar = document.querySelector('.dbtfy-announcement-bar .announcement-contents');
  }

  const header = document.querySelector('.dbtfy-header');

  const headerHeight = header?.offsetHeight | header?.querySelector('sticky-header')?.offsetHeight;

  if (maxHeaderHeight < headerHeight) {
    maxHeaderHeight = headerHeight;
  }

  const isAnnouncementBarSticky = Debutify.widgets['announcement']?.sticky_bar;

  if (isAnnouncementBarSticky || (typeof announcementLoadAfterHeader !== 'undefined' && announcementLoadAfterHeader)) {
    maxHeaderHeight = headerHeight + annaBar.offsetHeight;
  }

  let totalPrevHeightHeader = 0;
  let prevElem = getPreviousElementSiblings(header, 'shopify-section');

  let totalPrevHeightAnnouncementBar = 0;
  let prevAnnouncementBar = getPreviousElementSiblings(annaBar, 'shopify-section');

  while (prevElem) {
    totalPrevHeightHeader += prevElem.offsetHeight;
    prevElem = getPreviousElementSiblings(prevElem, 'shopify-section');
  }

  while (prevAnnouncementBar) {
    if (prevAnnouncementBar.offsetHeight) {
      totalPrevHeightAnnouncementBar += prevAnnouncementBar.offsetHeight;
    } else if (prevAnnouncementBar.classList.contains('section-header')) {
      totalPrevHeightAnnouncementBar += prevAnnouncementBar.querySelector('sticky-header')?.offsetHeight;
    }

    prevAnnouncementBar = getPreviousElementSiblings(prevAnnouncementBar, 'shopify-section');
  }

  if (Debutify.widgets['header']?.sticky_header_type && Debutify.widgets['header']?.sticky_header_type != 'none') {
    if (Debutify.widgets['header'].header_transparent && document.querySelector('body.template-index')) {
      header.classList.add('header--transparent');
      document.querySelector('main').style.marginTop = `-${maxHeaderHeight}px`;
    }

    if (isAnnouncementBarSticky) {
      header.style.setProperty('--height-header', `${totalPrevHeightHeader}px`);
    } else {
      header.style.setProperty('--height-header', `0px`);
    }
  } else {
    header?.style.setProperty('--height-header', '0px');
  }

  if (isAnnouncementBarSticky) {
    annaBar?.style.setProperty('--height--annauncement-bar', `${totalPrevHeightAnnouncementBar}px`);
  } else {
    annaBar?.style.setProperty('--height--annauncement-bar', '0px');
  }
  document.body.classList.add('header-loaded');

  const header_type = Debutify.widgets['header']?.sticky_header_type;
  if (header_type == 'reduce-logo-size' || header_type == 'always') {
    document.body.style.setProperty('--sticky-header-height', `${maxHeaderHeight}px`);
  } else {
    document.body.style.setProperty('--sticky-header-height', `0px`);
  }
}

if (Debutify.settings.dbtfy_sticky_header != false) {
  window.addEventListener('load', adjustHeaderBasedOnAnnaHeadergroup);
  window.addEventListener('resize', adjustHeaderBasedOnAnnaHeadergroup);
  window.addEventListener('scroll', adjustHeaderBasedOnAnnaHeadergroup);
} else {
  document.body.style.setProperty('--sticky-header-height', `0px`);
  document.body.classList.add('header-loaded');
}

if (!customElements.get('card-product-variant-selects')) {
  customElements.define(
    'card-product-variant-selects',
    class extends HTMLElement {
      constructor() {
        super();
        this.querySelectorAll('.swatch-input__input').forEach((input) => {
          input.addEventListener('click', (e) => {
            const mediaId = e.target.dataset.mediaId;
            const mediaImage = e.target.dataset.mediaImage;

            const carousal = e.target.closest('.card--card').querySelector('product-card-slider').dataset[
              'showCarousel'
            ];

            const secondaryImage = e.target.closest('.card--card').querySelector('product-card-slider').dataset[
              'showSecondaryImage'
            ];
            const cardMedia = input.closest('.card-wrapper').querySelector('.card__media');

            const media = cardMedia.querySelector(`img[media-id="${mediaId}"]`);
            if (carousal === 'true') {
              const index = Array.from(cardMedia.querySelectorAll('img')).indexOf(media) - 1;
              input.closest('.card--card').querySelector('product-card-slider').revealImage(index);
            } else if (secondaryImage === 'true') {
              const image = cardMedia.querySelectorAll('img')[1];
              if (image) {
                image.srcset = mediaImage;
                image.src = mediaImage;
              }
            } else {
              const image = cardMedia.querySelector('img');
              if (image) {
                image.srcset = mediaImage;
                image.src = mediaImage;
              }
            }
          });
        });
      }
    }
  );
}

if (!customElements.get('add-to-cart-product')) {
  customElements.define(
    'add-to-cart-product',
    class extends HTMLElement {
      constructor() {
        super();
        this.id = this.dataset.productId;
        this.querySelector('.button_add_to_cart')?.addEventListener('click', (e) => {
          e.preventDefault();
          Debutify.ajaxAddToCart({
            id: Number(this.id),
            quantity: 1,
          });
        });
      }
    }
  );
}
if (!window.DbtfyDiscountCode) {
  /**
   * Usage: publish(PUB_SUB_EVENTS.applyDiscount, { discountCode: 'DISCOUNT_CODE' });
   */
  window.DbtfyDiscountCodeInitialized = false;
  class DbtfyDiscountCode extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (!window.DbtfyDiscountCodeInitialized && Debutify.settings.apply_auto_discount) {
        this.initDiscount();
        //console.log('called', window.DbtfyDiscountCode);
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
      }
    }

    initDiscount() {
      window.DbtfyDiscountCodeInitialized = true;

      this.unsubscribeDiscount = subscribe(PUB_SUB_EVENTS.applyDiscount, (event) => {
        const discountCode = event.discountCode;
        if (!discountCode) return;

        fetch(`/discount/${discountCode}`, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
          .then((response) => {
            if (!response.ok) {
              console.error('Failed to apply discount: ' + response.statusText);
            }
          })
          .catch((error) => {
            console.error('Error applying discount:', error);
          })
          .finally(() => {
            if (Debutify.settings.cart_type === 'page') {
              window.location.reload();
            } else if (Debutify.settings.cart_type === 'drawer') {
              fetch(`${routes.cart_url}?section_id=cart-drawer`)
                .then((response) => response.text())
                .then((responseText) => {
                  const html = new DOMParser().parseFromString(responseText, 'text/html');
                  const thisDrawer = html.querySelector('#shopify-section-cart-drawer #CartDrawer');
                  document.querySelector('#shopify-section-cart-drawer #CartDrawer').replaceWith(thisDrawer);
                })
                .catch((e) => {
                  console.error(e);
                });
            }
          });
      });
    }

    setTotalQuantity() {
      const productID = this.addedProductID;
      const cartItems = this.cartItems;

      let totalQuantity = 0;
      cartItems.forEach((item) => {
        if (item.product_id == productID) {
          totalQuantity += item.quantity;
        }
      });
      this.totalAddedQuantity = totalQuantity;
    }

    async handleCartUpdate(e) {
      try {
        this.cartItems = e.cartData.items;

        if (e.cartData.items_added.length != 0) {
          this.addedProductID = e.cartData.items_added[0].product_id;
          this.addedProductHandle = e.cartData.items_added[0].url.split('?')[0];
        } else {
          this.addedProductID = e.cartData.items_removed[0].product_id;
          this.addedProductHandle = e.cartData.items_removed[0].url.split('?')[0];
        }

        this.setTotalQuantity();

        const productBreak = Debutify.widgets['qty-breaks']?.product_break;
        const cartBreak = Debutify.widgets['qty-breaks']?.cart_break;

        this.cartTotalPrice = e.cartData.original_total_price;
        this.cartItemCount = e.cartData.item_count;

        this.highestDiscountCode = null;
        this.highestDiscountAmount = 0;
        this.highestDiscountType = '%';

        for (let cartI = 0; cartI < cartBreak.length; cartI++) {
          for (let i = 1; i <= 10; i++) {
            const range = cartBreak[cartI][`cart_break_range_${i}`];
            const code = cartBreak[cartI][`cart_break_discount_code_${i}`];
            const type = cartBreak[cartI][`cart_break_discount_type_${i}`];
            const amountStr = cartBreak[cartI][`cart_break_discount_amount_${i}`];
            if (this.cartItemCount >= range && amountStr) {
              const amount = parseFloat(amountStr);
              if (!isNaN(amount) && amount > 0) {
                let discountValue = 0;
                if (type === '%') {
                  discountValue = (amount / 100) * this.cartTotalPrice;
                } else {
                  discountValue = amount;
                }

                if (discountValue > this.highestDiscountAmount) {
                  this.highestDiscountAmount = discountValue;
                  this.highestDiscountCode = code || '(no code)';
                  this.highestDiscountType = type;
                }
              }
            }
          }
        }

        //console.log('CB: cartTotalPrice', this.cartTotalPrice);
        //console.log('CB: cartItemCount', this.cartItemCount);
        //console.log('CB: Highest discount code:', this.highestDiscountCode);
        //console.log('CB: Highest discount amount:', this.highestDiscountAmount);
        //console.log('CB: Highest discount type:', this.highestDiscountType);

        //console.log(productBreak);
        await this.filterVisibility(productBreak);
        this.filterValidatedQuantity();

        //console.log('Final Discount, please apply this before expired: ', this.highestDiscountCode);
        publish(PUB_SUB_EVENTS.applyDiscount, {
          discountCode: this.highestDiscountCode,
        });
      } catch (error) {
        //console.log('Error: ' + error);
      }
    }

    async filterVisibility(items) {
      try {
        let data = await Debutify.fetchProductMarkup(`${this.addedProductHandle}?view=internal-full-product-details`);
        if (data) {
          // string convert to json
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');
          const productData = doc.querySelector('body').innerHTML;
          const productJson = JSON.parse(productData);

          const product = productJson.product;
          const collection = productJson.collections;
          //console.log('collection', collection);

          const collection_handles = collection.map((item) => item.handle);

          const filteredItems = items.filter((settings) => {
            switch (settings.product_break_visibility) {
              case 'collection':
                return collection_handles.includes(settings.product_break_collection);
              case 'type':
                return settings.product_break_types.toLowerCase().includes(product.type.toLowerCase());
              case 'tag':
                return product.tags.some((tag) =>
                  settings.product_break_tags.toLowerCase().includes(tag.toLowerCase())
                );
              case 'product':
                return settings.product_break_product === product.handle;
              case 'all':
                return true;
              default:
                return false;
            }
          });

          this.filteredItems = filteredItems;
        }
      } catch (error) {
        console.error('Error: ' + error);
        return null;
      }
    }

    filterValidatedQuantity() {
      if (this.filteredItems && this.filteredItems.length > 0) {
        //console.log('this.filteredItems', this.filteredItems);
        //console.log('this.totalAddedQuantity', this.totalAddedQuantity);

        for (const item of this.filteredItems) {
          for (const breakItem of item.breaks) {
            if (this.totalAddedQuantity >= breakItem.product_break_range) {
              const discountAmount = parseFloat(breakItem.product_break_discount_amount);

              if (!isNaN(discountAmount) && discountAmount > 0) {
                const discountValue =
                  breakItem.product_break_discount_type === '%'
                    ? (discountAmount / 100) * this.cartTotalPrice
                    : discountAmount;

                if (discountValue > this.highestDiscountAmount) {
                  this.highestDiscountAmount = discountValue;
                  this.highestDiscountCode = breakItem.product_break_discount_code || '(no code)';
                  this.highestDiscountType = breakItem.product_break_discount_type;
                }
              }
            }
            //console.log('Loop in');
          }
        }

        //console.log('CB: cartTotalPrice', this.cartTotalPrice);
        //console.log('CB: cartItemCount', this.cartItemCount);
        //console.log('CB: Highest discount code:', this.highestDiscountCode);
        //console.log('CB: Highest discount amount:', this.highestDiscountAmount);
        //console.log('CB: Highest discount type:', this.highestDiscountType);
      } else {
        return null;
      }
    }
  }
  // Attach the class to the global `window` object
  window.DbtfyDiscountCode = DbtfyDiscountCode;
}
